import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Breadcrumb, Select, Tag, Button, Badge, Drawer, Tooltip } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { adminHomeBcmb } from "src/static/data/breadcrumb_items";
import { MainContext } from "src/context";
import { Container } from "src/components/container/Container";
import PaginationFilter from "src/components/filter/PaginationFilter";
import { Table } from "antd";
import {
  GetQuizCollectionStatisticsConfig,
  GetQuizCollectionStatisticsExcelConfig,
  GetQuizRulesListConfig,
} from "src/service/api";
import { QuizCollectionStatisticsTypes, QuizRules } from "src/static/interface";
import "src/styles/statistics/index.scss";
import { openLink } from "src/utils";

function AdminStatisticsPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<QuizCollectionStatisticsTypes[]>(
    []
  );
  const [totalElements, setTotalElements] = useState<number>(0);
  const quizRuleId = searchParams.get("quizRuleId") || undefined;
  const { handleMakeParams } = useContext(MainContext);
  const [quizRules, setQuizRules] = useState<QuizRules[]>();
  const [answersDrawerVisible, setAnswersDrawerVisible] = useState(false);
  const [selectedUserAnswers, setSelectedUserAnswers] =
    useState<QuizCollectionStatisticsTypes | null>(null);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const { Option } = Select;

  const getFunc = () => {
    if (!quizRuleId) {
      setDataSource([]);
      setTotalElements(0);
      return;
    }

    setLoading(true);
    const page = searchParams.get("page") || "1";
    const size = searchParams.get("size") || "20";

    GetQuizCollectionStatisticsConfig(Number(quizRuleId), {
      page: Number(page),
      size: Number(size),
    })
      .then((res) => {
        const arr = res.data.content;
        arr.forEach((item: any, index: number) => {
          item.key = index + 1 + (Number(page) - 1) * Number(size);
        });
        setDataSource(arr);
        const total = res.data.totalElements;
        setTotalElements(total);
        window.scrollTo(0, 0);
      })
      .finally(() => setLoading(false));
  };

  const getFuncQuizRules = () => {
    GetQuizRulesListConfig().then((res) => {
      setQuizRules(res.data);
    });
  };

  useEffect(() => {
    getFuncQuizRules();
  }, []);

  useEffect(() => {
    getFunc();
  }, [searchParams, quizRuleId]);

  const handleViewDetails = (record: QuizCollectionStatisticsTypes) => {
    setSelectedUserAnswers(record);
    setAnswersDrawerVisible(true);
  };

  const userColumns: ColumnsType<QuizCollectionStatisticsTypes> = [
    {
      title: "№",
      dataIndex: "key",
      key: "key",
      width: 60,
      fixed: "left",
      align: "center",
    },
    {
      title: "FISh",
      dataIndex: "fullName",
      key: "fullName",
      width: 200,
      fixed: "left",
    },
    {
      title: "Universitet",
      dataIndex: "universityName",
      key: "universityName",
      width: 150,
    },
    {
      title: "Viloyat",
      dataIndex: "region",
      key: "region",
      width: 120,
      align: "center",
    },
    {
      title: "Tuman",
      dataIndex: "district",
      key: "district",
      width: 120,
      align: "center",
    },
    {
      title: "Manzil",
      dataIndex: "address",
      key: "address",
      width: 180,
    },
    {
      title: "Mutaxassislik",
      dataIndex: "speciality",
      key: "speciality",
      width: 150,
    },
    {
      title: "Kurs",
      dataIndex: "course",
      key: "course",
      width: 80,
      align: "center",
    },
    {
      title: "Ta'lim turi",
      dataIndex: "educationTypeName",
      key: "educationTypeName",
      width: 120,
      align: "center",
    },
    {
      title: "Ta'lim shakli",
      dataIndex: "eduForm",
      key: "eduForm",
      width: 120,
      align: "center",
    },
    {
      title: "Holati",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status: string) => {
        let color = "default";
        if (status === "Qatnashgan") color = "green";
        else if (status === "Jarayonda") color = "blue";
        else if (status === "Boshlanmagan") color = "orange";

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Javoblar",
      dataIndex: "answers",
      key: "answers",
      width: 120,
      align: "center",
      render: (answers: any[]) => (
        <div className="statistics__badge">
          <Badge count={answers?.length || 0} showZero />
        </div>
      ),
    },
    {
      title: "Amallar",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Savol va javoblarni ko'rish" placement="left">
          <Button
            type="primary"
            size="middle"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  const handleChange = (val: string) => {
    handleMakeParams?.("quizRuleId", val || "");
  };

  const downloadExcel = () => {
    setLoadingExcel(true);
    GetQuizCollectionStatisticsExcelConfig(Number(quizRuleId))
      .then((res) => {
        openLink(
          URL.createObjectURL(res.blob),
          "Talabalarning savollariga javoblari excel"
        );
      })
      .finally(() => setLoadingExcel(false));
  };

  return (
    <div>
      <Breadcrumb
        items={[
          adminHomeBcmb,
          {
            title: "Statistikalar",
          },
        ]}
      />
      <Container
        title="Statistikalar"
        extra={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Select
              value={quizRuleId}
              allowClear={true}
              showSearch
              placeholder="So'rovnomani tanlang"
              onChange={handleChange}
              style={{ width: 700 }}
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              onClear={() => handleChange("")}
            >
              {quizRules?.map((item) => (
                <Option key={item.id} value={String(item.id)}>
                  {item.theme}
                </Option>
              ))}
            </Select>
            <Button
              onClick={downloadExcel}
              icon={<DownloadOutlined />}
              type="primary"
              disabled={!quizRuleId}
              className="excel"
              loading={loadingExcel}
            >
              Excel yuklash
            </Button>
          </div>
        }
      >
        <Table
          columns={userColumns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          scroll={{ x: 800, y: 600 }}
          size="small"
          bordered
          sticky={{ offsetHeader: 64 }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <div>
            <strong>
              Jami: <span>{totalElements}</span>
            </strong>
          </div>
          <PaginationFilter total={totalElements} />
        </div>
      </Container>

      {/* Javoblar Drawer */}
      <Drawer
        title={
          <div className="statistics__drawer__title">
            <span>{selectedUserAnswers?.fullName}</span>
            <Tag
              color={
                selectedUserAnswers?.status === "Qatnashgan"
                  ? "green"
                  : selectedUserAnswers?.status === "Jarayonda"
                  ? "blue"
                  : "orange"
              }
            >
              {selectedUserAnswers?.status}
            </Tag>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <Tag color="blue">
                Jami: {selectedUserAnswers?.answers?.length || 0}
              </Tag>
              <Tag color="green">
                Belgilangan:{" "}
                {selectedUserAnswers?.answers?.filter(
                  (answer) =>
                    answer.answer !== null &&
                    answer.answer !== undefined &&
                    answer.answer !== ""
                ).length || 0}
              </Tag>
              <Tag color="red">
                Belgilanmagan:{" "}
                {selectedUserAnswers?.answers?.filter(
                  (answer) =>
                    answer.answer === null ||
                    answer.answer === undefined ||
                    answer.answer === ""
                ).length || 0}
              </Tag>
            </div>
          </div>
        }
        open={answersDrawerVisible}
        onClose={() => setAnswersDrawerVisible(false)}
        width={800}
        placement="right"
      >
        {selectedUserAnswers && (
          <div>
            {selectedUserAnswers.answers &&
            selectedUserAnswers.answers.length > 0 ? (
              <div className="statistics__answers">
                {selectedUserAnswers.answers.map((answer, index) => (
                  <div key={index} className="statistics__answers__card">
                    <div className="statistics__answers__card__content">
                      <div className="statistics__answers__card__number">
                        {index + 1}
                      </div>
                      <div className="statistics__answers__card__text">
                        <div
                          className="statistics__answers__card__question"
                          dangerouslySetInnerHTML={{ __html: answer.quiz }}
                        />
                        <div
                          className={`statistics__answers__card__answer ${
                            answer.answer === null ||
                            answer.answer === undefined ||
                            answer.answer === ""
                              ? "statistics__answers__card__answer--empty"
                              : "statistics__answers__card__answer--filled"
                          }`}
                        >
                          {answer.answer === null ||
                          answer.answer === undefined ||
                          answer.answer === ""
                            ? "❌ Belgilanmagan"
                            : `✅ ${
                                typeof answer.answer === "string"
                                  ? answer.answer
                                  : JSON.stringify(answer.answer)
                              }`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="statistics__empty">
                <QuestionCircleOutlined className="statistics__empty__icon" />
                <div>Hali javoblar mavjud emas</div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default AdminStatisticsPage;
